import React, { useEffect, useMemo, useState } from "react";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import CashFlow from "../MyTracker/CashFlow";
import { IActuals, ICashFlow } from "../../../interface/cashFlow";
import { REIProgressSpinner } from "../../../components/ui";
import { RadioButton } from "primereact/radiobutton";

const CashFlowSummaryGraph: React.FC<{
  isShowingTotals: boolean;
  selectedProfileId: number[];
  selectedEntityId: number;
  isDataLoading: boolean;
  year?: number;
  onYearChange?: (year: number) => void;
  cashFlowData?: ICashFlow;
}> = ({ selectedEntityId, selectedProfileId, isShowingTotals, isDataLoading, year, onYearChange, cashFlowData }) => {
  const [selectedProperty, setSelectedProperty] = useState<number>(1);

  useEffect(() => {
    setSelectedProperty(1);
  }, []);

  const filteredData: {
    Actuals: IActuals[];
    Forecast: IActuals[];
  } = useMemo(() => {
    if (!cashFlowData) return { Actuals: [], Forecast: [] };

    const entityIdToCompare = selectedEntityId === 0 ? "" : selectedEntityId.toString();
    let filteredData: { Actuals: IActuals[]; Forecast: IActuals[] } = { Actuals: [], Forecast: [] };
    if (selectedProperty.toString() === "1") {
      const graphData = cashFlowData.TotalForGraph?.find((total) => total.EntityId === entityIdToCompare);
      if (graphData) {
        filteredData = isShowingTotals
          ? {
              Actuals: graphData.Totals.Actuals,
              Forecast: graphData.Totals.Forecast,
            }
          : {
              Actuals: graphData.Portions.Actuals,
              Forecast: graphData.Portions.Forecast,
            };
      }
    } else {
      const propertyData = cashFlowData.PropertyDetails.find(
        (property) => property.PropertyDetail.PortfolioTrackerId.toString() === selectedProperty.toString()
      );
      if (isShowingTotals) {
        if (propertyData) {
          filteredData = {
            Actuals: propertyData.CashflowGraphTotals.Actuals,
            Forecast: propertyData.CashflowGraphTotals.Forecast,
          };
        }
      } else {
        if (propertyData) {
          filteredData = {
            Actuals: propertyData.CashflowGraphPortions.Actuals,
            Forecast: propertyData.CashflowGraphPortions.Forecast,
          };
        }
      }
    }

    return filteredData;
  }, [cashFlowData, selectedEntityId, isShowingTotals, selectedProperty]);

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProperty(Number(event.target.value));
  };

  const onYearChangeCallback = (newYear: number) => {
    if (onYearChange) {
      onYearChange(newYear);
    }
  };

  const renderContent = (
    <>
      {isDataLoading && <REIProgressSpinner />}
      {!isDataLoading && !cashFlowData && <div className="text-center text-gray-500">No cash flow data available.</div>}
      {!isDataLoading && cashFlowData && (
        <div className="flex flex-col lg:flex-row flex-wrap gap-4 justify-around h-[600px]">
          <div className="flex-1 w-1/2 h-full">
            <CashFlow
              trackerData={filteredData}
              showPostExpenseData={selectedProperty.toString() === "1"}
              entityId=""
              showingTotalSummary={false}
              year={year}
              onYearChangeCallback={onYearChangeCallback}
            ></CashFlow>
          </div>
          <div className="flex-1 w-1/2 flex-col gap-2 h-full justify-center">
            <div className="font-semibold text-xl pb-2">Showing cash flow summary for:</div>
            <div className="max-h-[500px] overflow-y-auto">
              <div className="text-left text-base text-slate-500">
                <div className="flex gap-3 mb-2">
                  <div className="flex gap-3">
                    <RadioButton
                      inputId="property-total"
                      type="radio"
                      id="property-total"
                      name="property"
                      value="1"
                      data-testid="cfsg-rdo-allproperties"
                      checked={selectedProperty.toString() === "1"}
                      onChange={(e) => setSelectedProperty(e.value)}
                    />
                    <label htmlFor="property-total" className="ml-2">
                      All properties
                    </label>
                  </div>
                </div>
                {cashFlowData?.PropertyDetails?.map((property, index) => {
                  const address = [
                    property.PropertyDetail.StreetAddress,
                    property.PropertyDetail.Suburb,
                    property.PropertyDetail.District,
                    property.PropertyDetail.State,
                    property.PropertyDetail.Postcode,
                  ]
                    .filter(Boolean)
                    .join(", ");

                  const addressWithCountry = `${address}${property.PropertyDetail.Country ? ` (${property.PropertyDetail.Country})` : ""}`;

                  const uniqueId = `${property.PropertyDetail.PortfolioTrackerId}-${index}`;

                  return (
                    <div key={uniqueId} className="flex items-center gap-3 mb-2">
                      <RadioButton
                        inputId={uniqueId}
                        name="property"
                        value={property.PropertyDetail.PortfolioTrackerId}
                        data-testid={`cfsg-rdo-property-${uniqueId}`}
                        onChange={(e) => setSelectedProperty(e.value)}
                        checked={selectedProperty.toString() + "-" + index === uniqueId}
                      />
                      <label htmlFor={uniqueId} className="ml-2 break-words truncate">
                        {addressWithCountry}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <REIToggablePanel
      contentComponent={renderContent}
      defaultCollapse={false}
      header="Cash Flow Summary"
      istoggleable={false}
      customClassName="align-middle bg-gray-100"
    ></REIToggablePanel>
  );
};

export default CashFlowSummaryGraph;
